import React from "react"

import chromeInstructions from "assets/cloudroom-chrome.png"
import safariInstructions from "assets/cloudroom-safari.png"
import { BrowserInfo } from "detect-browser"

import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline"

export type DevicePermissionsInstructionsProps = {
  instructionsOpen: "browser" | "camera" | "mic"
  browser: BrowserInfo
  userType: "Tutor" | "Student" | "Teacher" | "Admin"
}

const DevicePermissionsInstructions: React.FC<
  DevicePermissionsInstructionsProps
> = ({ instructionsOpen, browser, userType }) => {
  const recommendedBrowser = browser?.os === "iOS" ? "Safari" : "Google Chrome"

  const supportInstructions = (): React.ReactNode => {
    switch (userType) {
      case "Student":
        return (
          <li>
            If you have already completed the steps above, click the "Contact
            Support" button below to be contacted by a member of our team.
          </li>
        )
      case "Tutor":
        return (
          <li>
            If you have already completed the steps above, please reach out to
            your admin support channel in slack.
          </li>
        )
      case "Teacher":
        return (
          <li>
            If you have already completed the steps above, please call
            800-211-1986 for support.
          </li>
        )
      default:
        return null
    }
  }

  return (
    <>
      {instructionsOpen === "browser" ? (
        <ol className="pl-5 [&>li]:list-decimal">
          <li>
            Make sure {recommendedBrowser} is installed on your device. You are
            currently on {browser?.name}. If it is not, you can download it{" "}
            {recommendedBrowser === "Safari" ? (
              "in the app store."
            ) : (
              <a
                href="https://www.google.com/chrome/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex cursor-pointer items-center text-sky-600 hover:underline"
              >
                <span>here.</span>
                <ArrowTopRightOnSquareIcon className="ml-1 inline h-4 w-4" />
              </a>
            )}
          </li>
          <li>Copy the current url.</li>
          <li>
            Open {recommendedBrowser} and paste the url into the search bar.
            Make sure you have your Air Tutors login credentials ready in case
            you need to log in again.
          </li>
        </ol>
      ) : (
        (instructionsOpen === "camera" || instructionsOpen === "mic") && (
          <ol className="pl-5 [&>li]:list-decimal">
            {browser?.name === "ios" ? (
              <>
                <li>
                  Right Click (or hold control and click): URL Bar as shown in
                  the picture
                </li>
                <li>
                  Click: Settings for This Website (or ... symbol on mobile)
                </li>
                <li>Find Camera and Microphone options near bottom</li>
                <li>Change option to Allow</li>
                <li>Refresh the page</li>
                {supportInstructions()}
              </>
            ) : (
              <>
                <li>
                  At the top of your browser, click the Settings Icon (circled
                  in the picture)
                </li>
                <li>Toggle Camera, Microphone and Sound to on</li>
                <li>Click away from the settings box</li>
                <li>
                  Click the "Reload" button that appears, or manually refresh
                </li>
                {supportInstructions()}
              </>
            )}

            <div className="mt-6">
              {browser?.name === "ios" ? (
                <img src={safariInstructions} alt="Cloud Room Instructions" />
              ) : (
                <img src={chromeInstructions} alt="Cloud Room Instructions" />
              )}
            </div>
          </ol>
        )
      )}
    </>
  )
}

export default DevicePermissionsInstructions
